import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { formatMoney, formatQty, UNITS, WASTAGE_REASONS, type CurrencyCode, type WastageReason } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import { useCurrentBranch } from '../../app/Shell';
import { Button, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, TextInput, fieldErrors, formatDateTime, useToast } from '../../ui';
import { BarList } from '../../ui/charts';
import { PeriodPicker, ProductQtyLines, usePeriod, type QtyLine } from '../../ui/period';

interface WastageReport {
  byReason: { reason: string; label: string; valueCents: number; lines: number }[];
  byProduct: { name: string; unit: string; qtyMilli: number; valueCents: number }[];
  totalCents: number;
  records: { id: string; number: string; createdAt: string; branchName: string; userName: string | null; reasonLabel: string; note: string | null; valueCents: number }[];
}

export function WastagePage() {
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const period = usePeriod('30d');
  const [recording, setRecording] = useState(false);
  const q = useQuery({
    queryKey: ['wastage', period.from, period.to],
    queryFn: () => api.get<WastageReport>(`/api/wastage?from=${period.from}&to=${period.to}`),
  });
  const money = (c: number) => formatMoney(c, cur);
  return (
    <>
      <PageHeader
        title="Wastage"
        description="Stock thrown away: trim, spoilage, damage, expiry. It leaves stock at average cost and shows as a cost in the profit report."
        actions={can('wastage.record') && <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setRecording(true)}>Record wastage</Button>}
      />
      <div className="mb-4"><PeriodPicker period={period} /></div>
      {q.isLoading && <Spinner />}
      {q.error && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.data && (
        <div className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
            <Panel title="By reason">
              <div className="p-5">
                <p className="mb-4 text-sm text-muted">
                  Total <span className="text-2xl font-semibold text-ink num">{money(q.data.totalCents)}</span> at cost
                </p>
                {q.data.byReason.length ? (
                  <BarList items={q.data.byReason.map((r) => ({ label: r.label, value: r.valueCents, note: `${r.lines} line${r.lines === 1 ? '' : 's'}` }))} format={money} />
                ) : (
                  <p className="text-muted">No wastage in this period.</p>
                )}
              </div>
            </Panel>
            <Panel title="Most wasted products">
              {q.data.byProduct.length === 0 ? (
                <EmptyState title="Nothing wasted in this period" />
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-line text-sm text-muted">
                      <th className="px-5 py-2.5 font-medium">Product</th>
                      <th className="px-3 py-2.5 text-right font-medium">Quantity</th>
                      <th className="px-5 py-2.5 text-right font-medium">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {q.data.byProduct.map((p) => (
                      <tr key={p.name}>
                        <td className="px-5 py-2.5">{p.name}</td>
                        <td className="px-3 py-2.5 text-right num">{formatQty(p.qtyMilli)} {UNITS[p.unit as keyof typeof UNITS]?.short}</td>
                        <td className="px-5 py-2.5 text-right num">{money(p.valueCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>
          <Panel title="Recent wastage records">
            {q.data.records.length === 0 ? (
              <EmptyState title="No wastage recorded yet" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left">
                  <thead>
                    <tr className="border-b border-line text-sm text-muted">
                      <th className="px-5 py-2.5 font-medium">Record</th>
                      <th className="px-3 py-2.5 font-medium">Reason</th>
                      <th className="px-3 py-2.5 font-medium">Recorded by</th>
                      <th className="px-5 py-2.5 text-right font-medium">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {q.data.records.map((r) => (
                      <tr key={r.id}>
                        <td className="px-5 py-2.5">
                          <span className="font-medium">{r.number}</span>
                          <span className="block text-xs text-muted">{formatDateTime(r.createdAt)} — {r.branchName}</span>
                        </td>
                        <td className="px-3 py-2.5 text-sm">
                          {r.reasonLabel}
                          {r.note && <span className="block text-xs text-muted">{r.note}</span>}
                        </td>
                        <td className="px-3 py-2.5 text-sm">{r.userName ?? '—'}</td>
                        <td className="px-5 py-2.5 text-right num">{money(-r.valueCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      )}
      {recording && <WastageDialog onClose={() => setRecording(false)} />}
    </>
  );
}

function WastageDialog({ onClose }: { onClose: () => void }) {
  const { me } = useSession();
  const { branch } = useCurrentBranch();
  const qc = useQueryClient();
  const toast = useToast();
  const [branchId, setBranchId] = useState(branch?.id ?? '');
  const [reason, setReason] = useState<WastageReason>(me.business.businessType === 'butchery' ? 'trim' : 'damaged');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<QtyLine[]>([]);
  const m = useMutation({
    mutationFn: () => api.post<{ number: string }>('/api/wastage', { branchId, reason, note, lines: lines.map((l) => ({ productId: l.product.id, qtyMilli: l.qtyMilli })) }),
    onSuccess: (r) => {
      toast(`${r.number} recorded`);
      for (const k of ['wastage', 'products', 'expiry', 'dashboard']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  const ready = branchId && lines.length > 0 && lines.every((l) => l.qtyMilli);
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title="Record wastage"
      description="Weigh or count what is being thrown away. It is taken out of stock at average cost."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ready} busy={m.isPending} onClick={() => m.mutate()}>Record wastage</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="mb-4 grid gap-4 sm:grid-cols-3">
        <Field label="Branch" htmlFor="w-branch">
          <Select id="w-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="Reason" htmlFor="w-reason" error={f.reason}>
          <Select id="w-reason" value={reason} onChange={(e) => setReason(e.target.value as WastageReason)}>
            {Object.entries(WASTAGE_REASONS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </Select>
        </Field>
        <Field label="Note (optional)" htmlFor="w-note">
          <TextInput id="w-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. fridge 2 failed overnight" />
        </Field>
      </div>
      <ProductQtyLines lines={lines} onChange={setLines} branchId={branchId} pickerLabel="Products wasted" emptyText="Search for the products being thrown away." />
      {f.lines && <p className="mt-2 text-sm text-danger">{f.lines}</p>}
    </Dialog>
  );
}
